import { useState, useEffect } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Users, Search, Tag, Mail, Phone, Building2, Shield, ShieldOff, Trash2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const TAG_OPTIONS = ["Cliente frecuente", "Mayorista", "Corporativo", "Nuevo", "VIP", "Gobierno", "Educación"];

export default function AdminCRM() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [tags, setTags] = useState([]);

  const fetchClients = () => {
    API.get("/admin/clients").then((r) => { setClients(r.data); setLoading(false); }).catch(() => { setClients([]); setLoading(false); });
  };

  useEffect(() => { fetchClients(); }, []);

  const filtered = clients.filter((c) =>
    c.name?.toLowerCase().includes(search.toLowerCase()) ||
    c.email?.toLowerCase().includes(search.toLowerCase()) ||
    c.company?.toLowerCase().includes(search.toLowerCase())
  );

  const updateTags = async () => {
    await API.put(`/admin/clients/${selected._id}/tags`, { tags });
    setSelected(null);
    fetchClients();
  };

  const toggleTag = (tag) => {
    setTags((t) => t.includes(tag) ? t.filter((x) => x !== tag) : [...t, tag]);
  };

  const blockClient = async (id) => {
    await API.put(`/admin/clients/${id}/block`);
    toast.success("Cliente bloqueado");
    fetchClients();
  };

  const unblockClient = async (id) => {
    await API.put(`/admin/clients/${id}/unblock`);
    toast.success("Cliente desbloqueado");
    fetchClients();
  };

  const deleteClient = async (id, name) => {
    if (!window.confirm(`¿Eliminar a "${name}" y todas sus cotizaciones y pedidos? Esta acción no se puede deshacer.`)) return;
    await API.delete(`/admin/clients/${id}`);
    toast.success("Cliente eliminado");
    fetchClients();
  };

  return (
    <div className="p-6" data-testid="admin-crm-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0047AB] mb-2">CRM</p>
          <h1 className="text-2xl font-bold tracking-tight text-[#0F172A]" style={{ fontFamily: 'Chivo' }}>Clientes</h1>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar cliente..." className="pl-9 border-[#CBD5E1] rounded-sm w-64" data-testid="crm-search-input" />
        </div>
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-sm">
        {loading ? (
          <p className="p-8 text-sm text-[#94A3B8] text-center">Cargando...</p>
        ) : filtered.length === 0 ? (
          <p className="p-8 text-sm text-[#94A3B8] text-center">No se encontraron clientes</p>
        ) : (
          <div className="divide-y divide-[#E2E8F0]" data-testid="clients-list">
            {filtered.map((c) => (
              <div key={c._id} className={`px-5 py-4 flex items-center gap-4 ${c.blocked ? "opacity-50" : ""}`} data-testid={`client-row-${c._id}`}>
                {/* Avatar */}
                <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 text-sm font-bold ${c.blocked ? "bg-red-100 text-red-600" : "bg-[#F0F4FF] text-[#0047AB]"}`}>
                  {c.blocked ? <ShieldOff className="w-4 h-4" /> : c.name?.charAt(0)?.toUpperCase()}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-[#0F172A]">{c.name}</p>
                    {c.blocked && <span className="text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded-sm font-bold uppercase">Bloqueado</span>}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-[#475569] mt-0.5">
                    <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{c.email}</span>
                    {c.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{c.phone}</span>}
                    {c.company && <span className="flex items-center gap-1"><Building2 className="w-3 h-3" />{c.company}</span>}
                  </div>
                  {/* Tags */}
                  {(c.tags || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {c.tags.map((t) => (
                        <span key={t} className="bg-[#F0F4FF] text-[#0047AB] px-2 py-0.5 text-[10px] font-medium rounded-sm">{t}</span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Stats */}
                <div className="text-right text-xs text-[#475569] shrink-0">
                  <p>{c.quote_count} cotización(es)</p>
                  <p>{c.order_count} pedido(s)</p>
                  <p className="text-[#94A3B8] text-[10px] mt-0.5">Desde {new Date(c.created_at).toLocaleDateString()}</p>
                </div>

                {/* Actions */}
                <div className="flex gap-1 shrink-0">
                  <Button variant="ghost" size="sm" onClick={() => { setSelected(c); setTags(c.tags || []); }} className="rounded-sm" title="Etiquetas" data-testid={`edit-tags-${c._id}`}>
                    <Tag className="w-4 h-4 text-[#475569]" />
                  </Button>
                  {c.blocked ? (
                    <Button variant="ghost" size="sm" onClick={() => unblockClient(c._id)} className="rounded-sm" title="Desbloquear" data-testid={`unblock-${c._id}`}>
                      <Shield className="w-4 h-4 text-green-600" />
                    </Button>
                  ) : (
                    <Button variant="ghost" size="sm" onClick={() => blockClient(c._id)} className="rounded-sm" title="Bloquear" data-testid={`block-${c._id}`}>
                      <ShieldOff className="w-4 h-4 text-amber-500" />
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => deleteClient(c._id, c.name)} className="rounded-sm" title="Eliminar" data-testid={`delete-${c._id}`}>
                    <Trash2 className="w-4 h-4 text-red-400" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Tag Dialog */}
      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Chivo' }}>Etiquetas: {selected?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2">
              {TAG_OPTIONS.map((tag) => (
                <button key={tag} onClick={() => toggleTag(tag)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-sm border transition-colors ${tags.includes(tag) ? "bg-[#0047AB] text-white border-[#0047AB]" : "bg-white text-[#475569] border-[#E2E8F0] hover:border-[#0047AB]"}`}
                  data-testid={`tag-${tag}`}>
                  {tag}
                </button>
              ))}
            </div>
            <Button onClick={updateTags} className="w-full bg-[#0047AB] text-white font-bold rounded-sm hover:bg-[#003A8C]" data-testid="save-tags-btn">
              Guardar Etiquetas
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
